import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasRight } from '@aiinterview/shared';
import { api } from './client';
import { applyFavicon } from '../branding';

export interface CurrentUser {
    userName: string;
    fullName: string | null;
    roleCode: string;
    rights: string[];
    mustChangePassword: boolean;
}

export interface Branding {
    productName: string;
    companyName: string;
    logoPath: string;
    secondaryLogoPath: string;
    faviconPath: string;
    primaryColor: string;
    secondaryColor: string;
    supportEmail: string;
    footerText: string;
}

interface SessionValue {
    user: CurrentUser | null;
    branding: Branding | null;
    loading: boolean;
    can: (right: string) => boolean;
    refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();

    const userQuery = useQuery({
        queryKey: ['session'],
        queryFn: () => api.get<CurrentUser | null>('/api/auth/me'),
        // The cookie is the source of truth and can be revoked server-side at any moment, so this is
        // never served stale from cache on a fresh mount.
        staleTime: 0,
        retry: false
    });

    const brandingQuery = useQuery({
        queryKey: ['branding'],
        queryFn: () => api.get<Branding>('/api/branding'),
        staleTime: 5 * 60_000,
        retry: false
    });

    const branding = brandingQuery.data ?? null;

    // Applied as CSS custom properties rather than Tailwind tokens, because an administrator can
    // change them at runtime and a rebuild is not an option.
    if (branding) {
        const root = document.documentElement.style;
        root.setProperty('--brand-primary', branding.primaryColor);
        root.setProperty('--brand-secondary', branding.secondaryColor);
        // The tab icon travels with the colours. It arrived on this payload all along and was the
        // one branding field nothing read, which is why the admin app had no icon at all.
        applyFavicon(branding.faviconPath);
    }

    const value: SessionValue = {
        user: userQuery.data ?? null,
        branding,
        loading: userQuery.isLoading,
        can: (right: string) => hasRight(userQuery.data?.rights ?? [], right),
        refresh: async () => {
            await queryClient.invalidateQueries({ queryKey: ['session'] });
        }
    };

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
    const value = useContext(SessionContext);

    if (!value) {
        throw new Error('useSession must be used inside a SessionProvider.');
    }

    return value;
}
